/**
 * The task routes: /do/…
 *
 * One path per thing somebody wants to do, named after the doing rather than
 * the document. They sit beside the module screens rather than replacing
 * them: /do/sale and /transactions/new/SI make the same invoice, and which
 * one you open depends on whether you think in invoices.
 */

import { lazy, Suspense } from 'react';
import { Route } from 'react-router-dom';
import { RequirePermission } from '@/components/shared/RequirePermission';
import { Spinner } from '@/components/shared/states';

/**
 * Each flow is loaded on demand, so each needs somewhere to wait.
 *
 * Without this the router swaps a suspending element in during a click and
 * React refuses - the screen is replaced by the error boundary for what is
 * really just a chunk that has not arrived yet.
 */
const page = (node: React.ReactNode) => <Suspense fallback={<Spinner label="Loading…" />}>{node}</Suspense>;

const AddCustomerPage = lazy(() => import('./AddCustomer').then((m) => ({ default: m.AddCustomerPage })));
const MoveStockPage = lazy(() => import('./MoveStock').then((m) => ({ default: m.MoveStockPage })));
const AttendancePage = lazy(() => import('./Attendance').then((m) => ({ default: m.AttendancePage })));
const PayrollPage = lazy(() => import('./Payroll').then((m) => ({ default: m.PayrollPage })));

export function doRoutes() {
  return (
    <Route path="do">
      <Route
        path="customer"
        element={page(<RequirePermission module="master.party" action="create" what="customers"><AddCustomerPage /></RequirePermission>)}
      />
      <Route
        path="move-stock"
        element={page(<RequirePermission module="erp.warehouse.transfer" action="create" what="stock transfers"><MoveStockPage /></RequirePermission>)}
      />
      <Route path="attendance" element={page(<AttendancePage />)} />
      <Route
        path="payroll"
        element={page(<RequirePermission module="hr.payroll" what="payroll"><PayrollPage /></RequirePermission>)}
      />
    </Route>
  );
}
